import { useEffect, useState } from 'react';

import { apiFetch } from '../lib/apiFetch';

export interface HalLinks {
  [key: string]: { href: string };
}

interface RecordLinksResult {
  key: string;
  links: HalLinks;
  title: string | null;
  error: string | null;
  accessDenied: boolean;
}

interface UseRecordLinks {
  /** The HAL links advertised for the record, or version, once loaded. */
  links: HalLinks;
  /** A label for the record, for headings. */
  title: string | null;
  loading: boolean;
  error: string | null;
  accessDenied: boolean;
}

const NO_LINKS: HalLinks = {};

/**
 * Load a record, or one of its versions, and expose the HAL links it
 * advertises.
 *
 * Pages that act on a record without showing all of it follow these links
 * instead of building URLs, so what the API permits stays the API's decision.
 */
export function useRecordLinks(id?: string, versionId?: string): UseRecordLinks {
  // Keyed by what it was fetched for, so loading is derived rather than set
  // alongside the fetch, and a response for a record the reviewer has since
  // left is ignored.
  const [result, setResult] = useState<RecordLinksResult | null>(null);
  const key = `${id ?? ''}:${versionId ?? ''}`;

  useEffect(() => {
    if (!id) return;
    let cancelled = false;

    const url = versionId
      ? `/api/v1/records/${id}/versions/${versionId}`
      : `/api/v1/records/${id}`;

    const load = async (): Promise<RecordLinksResult> => {
      try {
        const response = await apiFetch(url);
        if (!response.ok) {
          const body = await response.json().catch(() => ({}));
          return {
            key,
            links: NO_LINKS,
            title: null,
            error: body.error || `Failed to load record (${response.status})`,
            accessDenied: response.status === 403,
          };
        }
        const data = await response.json();
        const record = data._embedded?.record;
        return {
          key,
          links: (data._links ?? NO_LINKS) as HalLinks,
          title: record?.title ?? record?.file?.name ?? null,
          error: null,
          accessDenied: false,
        };
      } catch (caught) {
        return {
          key,
          links: NO_LINKS,
          title: null,
          error: caught instanceof Error ? caught.message : 'Failed to load record',
          accessDenied: false,
        };
      }
    };

    void load().then((loaded) => {
      if (!cancelled) setResult(loaded);
    });

    return () => {
      cancelled = true;
    };
  }, [id, versionId, key]);

  if (!id) {
    return {
      links: NO_LINKS,
      title: null,
      loading: false,
      error: 'No record was specified',
      accessDenied: false,
    };
  }

  if (!result || result.key !== key) {
    return { links: NO_LINKS, title: null, loading: true, error: null, accessDenied: false };
  }

  return {
    links: result.links,
    title: result.title,
    loading: false,
    error: result.error,
    accessDenied: result.accessDenied,
  };
}
